import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SectionHead from './SectionHead';
import Wordmark from './Wordmark';
import LandingButton from './LandingButton';
import Reveal from './Reveal';
import useInView from './useInView';
import { BP, LANDING_FONT, LC, MAXW, MOTION, PAD_X, SECTION_Y, r } from './landingTokens';

// right column: array of text segments; b=true renders bold (evergreen).
const ROWS = [
  { left: 'One goal, then you start over.', right: [{ t: 'Unlimited stacks', b: true }, { t: ' — one for every goal you have.' }] },
  { left: 'Advice scattered across tabs and screenshots.', right: [{ t: 'Every stack saved and available to share', b: true }, { t: '.' }] },
  { left: 'Re-research every time your meds change.', right: [{ t: 'Auto-rechecked', b: true }, { t: ' as your profile changes.' }] },
  { left: 'Nutrient gaps from your meds and diet go unnoticed.', right: [{ t: 'Stacks that close the nutrient gaps', b: true }, { t: ' your meds and diet create.' }] },
  { left: 'Trying to decode research written for scientists.', right: [{ t: 'The full evidence library', b: true }, { t: ', easy to understand.' }] },
];

const gridColsSx = { display: 'grid', gridTemplateColumns: '1fr', [BP.tablet]: { gridTemplateColumns: '1fr 1fr' } };

const MarkNo = () => (
  <Box sx={{ flex: 'none', width: r(18), height: r(18), borderRadius: '50%', display: 'grid', placeItems: 'center', mt: r(1), border: `${r(1.5)} solid ${LC.border}` }}>
    <Box component="svg" width={9} height={9} viewBox="0 0 12 12" aria-hidden="true"><path d="M3 6h6" stroke={LC.inkMuted} strokeWidth="1.8" strokeLinecap="round" /></Box>
  </Box>
);
// The ✓ stroke-draws a beat after its row settles — the contrast with the
// static × does the arguing (softened per the "subtle" animation review).
const MarkYes = ({ delay = 0 }) => {
  const [ref, inView, instant] = useInView();
  // `stroke` is an SVG attribute, which `sx` does not resolve against the
  // palette, so the contrast value is read from the theme directly.
  const theme = useTheme();
  return (
    <Box ref={ref} sx={{ flex: 'none', width: r(18), height: r(18), borderRadius: '50%', display: 'grid', placeItems: 'center', mt: r(1), background: LC.accent }}>
      <Box component="svg" width={10} height={10} viewBox="0 0 14 14" aria-hidden="true">
        <Box
          component="path"
          d="M3 7.4l2.6 2.6L11 4.6"
          pathLength="1"
          fill="none"
          stroke={theme.palette.primary.contrastText}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{
            strokeDasharray: 1,
            strokeDashoffset: inView ? 0 : 1,
            transition: instant ? 'none' : `stroke-dashoffset ${MOTION.durationSm}ms ${MOTION.ease} ${delay}ms`,
            '@media (prefers-reduced-motion: reduce)': { strokeDashoffset: 0, transition: 'none' },
          }}
        />
      </Box>
    </Box>
  );
};

export default function Membership() {
  const navigate = useNavigate();
  return (
    <Box component="section" sx={{ background: LC.surface, borderBottom: `${r(1)} solid ${LC.border}`, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: MAXW, px: PAD_X, py: SECTION_Y }}>
        <Reveal repeat>
          <SectionHead
            eyebrow="Membership"
            title="What a membership gives you."
            lead="One membership, unlimited stacks — for less than a bottle of the wrong supplement."
            maxWidth={r(640)}
            leadMaxWidth="46ch"
          />
        </Reveal>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'clamp(1.875rem, 4vw, 2.5rem)' }}>
        <Box sx={{ width: '100%', maxWidth: r(880), border: `${r(1)} solid ${LC.border}`, borderRadius: r(20), overflow: 'hidden', background: LC.surface }}>
          {/* Head */}
          <Box sx={gridColsSx}>
            <Box sx={{ px: r(28), py: r(16), fontFamily: LANDING_FONT, fontSize: r(11), fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: LC.inkMuted }}>On your own</Box>
            <Box sx={{ px: r(28), py: r(16), background: LC.evergreen, borderLeft: `${r(1)} solid ${LC.border}`, display: 'flex', alignItems: 'center', gap: r(8) }}>
              <Box component="svg" width={18} height={18} viewBox="0 0 64 64" aria-hidden="true">
                <line x1="16" y1="20" x2="50" y2="14" stroke={LC.mint} strokeWidth="4" />
                <line x1="16" y1="20" x2="40" y2="46" stroke={LC.mint} strokeWidth="4" />
                <line x1="50" y1="14" x2="40" y2="46" stroke={LC.mint} strokeWidth="4" />
                <circle cx="16" cy="20" r="7.5" fill={LC.mint} /><circle cx="50" cy="14" r="7.5" fill={LC.mint} /><circle cx="40" cy="46" r="10" fill={LC.accent} />
              </Box>
              <Wordmark size={16} variant="onDark" />
            </Box>
          </Box>

          {/* Rows */}
          {ROWS.map((row, i) => (
            <Reveal key={i} delay={i * MOTION.stagger} repeat sx={{ ...gridColsSx, borderTop: `${r(1)} solid ${LC.border}` }}>
              <Box sx={{ px: r(28), py: r(20), display: 'flex', gap: r(12), alignItems: 'flex-start', background: LC.surface }}>
                <MarkNo />
                <Box component="p" sx={{ m: 0, fontFamily: LANDING_FONT, fontSize: r(14), lineHeight: 1.5, color: LC.inkSoft }}>{row.left}</Box>
              </Box>
              <Box sx={{ px: r(28), py: r(20), display: 'flex', gap: r(12), alignItems: 'flex-start', background: LC.surfaceTint, borderLeft: `${r(1)} solid ${LC.border}` }}>
                <MarkYes delay={i * MOTION.stagger + 240} />
                <Box component="p" sx={{ m: 0, fontFamily: LANDING_FONT, fontSize: r(14), lineHeight: 1.5, color: LC.ink }}>
                  {row.right.map((seg, j) => seg.b
                    ? <Box component="b" key={j} sx={{ fontWeight: 700, color: LC.evergreen }}>{seg.t}</Box>
                    : <React.Fragment key={j}>{seg.t}</React.Fragment>)}
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mt: r(28), display: 'flex', flexDirection: 'column', alignItems: 'center', gap: r(10) }}>
          <LandingButton onClick={() => navigate('/register')} sx={{ fontSize: r(16), px: r(30), py: r(15), borderRadius: r(13) }}>Start free trial</LandingButton>
          <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(12.5), color: LC.inkMuted }}>14-day free trial · cancel anytime</Box>
        </Box>
      </Box>
    </Box>
  );
}
